import * as React from "react";
import { useState } from "react";
import Dialog from "@imminently/decisively-ui/Dialog";
import Form from "@imminently/decisively-ui/Form";
import Button from "@imminently/decisively-ui/Button";
import Switch from "@imminently/decisively-ui/Switch";
import TextField from "@imminently/decisively-ui/TextField";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import { useDismissGuard } from "@imminently/decisively-ui/DismissGuard";
import { useFormState } from "@imminently/decisively-ui/Form";
import ConfirmDialog from "@imminently/decisively-ui/ConfirmDialog";
import Tabs from "@imminently/decisively-ui/Tabs";
import { notify } from "@/common/notifications";

import {
  ComponentPage,
  ComponentSection,
  ComponentPreview,
  ControlPanel,
  ControlRow,
} from "../components/ComponentPage";

// ─── Standalone guard demo ─────────────────────────────────────────────────

function StandaloneDirtyGuard() {
  const [active, setActive] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingProceed, setPendingProceed] = useState<(() => void) | null>(null);

  const { active: guardActive } = useDismissGuard({
    when: active,
    onAttempt: (proceed) => {
      setPendingProceed(() => proceed);
      setConfirmOpen(true);
    },
  });

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Switch
            checked={active}
            onCheckedChange={setActive}
          />
          <span className="text-sm font-medium">Guard active</span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-mono ${guardActive ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" : "bg-muted text-muted-foreground"}`}
          >
            {guardActive ? "🔒 active" : "unlocked"}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Toggle the guard on, then navigate away or close the browser tab — the guard will intercept.
        </p>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Unsaved changes"
        description="You have unsaved changes. Are you sure you want to leave?"
        confirmLabel="Leave anyway"
        cancelLabel="Stay"
        confirmIntent="destructive"
        onConfirm={() => {
          const proceed = pendingProceed;
          setConfirmOpen(false);
          setPendingProceed(null);
          proceed?.();
        }}
      />
    </>
  );
}

// ─── Form in dialog with guard ─────────────────────────────────────────────

function GuardedDialogForm() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        label="Open guarded dialog"
      >
        Open guarded dialog
      </Button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        intent="form"
      >
        {open && <GuardedDialogFormContent onSaved={() => setOpen(false)} />}
      </Dialog>
    </>
  );
}

function GuardedDialogFormContent({ onSaved }: { onSaved: () => void }) {
  return (
    <Dialog.Content className="flex flex-col w-full max-w-lg">
      <Form
        defaultValues={{ name: "", notes: "" }}
        dismissGuard
        onSubmit={async (values, methods) => {
          methods.reset(values);
          notify.success("Profile saved");
          onSaved();
        }}
      >
        <Dialog.Header>
          <Dialog.Title>Edit Profile</Dialog.Title>
          <Dialog.Description>Make changes and submit — or try closing with unsaved changes.</Dialog.Description>
          <Dialog.Controls />
        </Dialog.Header>

        <Dialog.Body>
          <FormPanel>
            <TextField
              name="name"
              label="Name"
              placeholder="Enter your name"
            />
            <TextField
              name="notes"
              label="Notes"
              placeholder="Any notes…"
            />
          </FormPanel>
          <DirtyIndicator />
        </Dialog.Body>

        <Dialog.Actions>
          <Dialog.Actions.CancelButton label="Cancel">Cancel</Dialog.Actions.CancelButton>
          <Dialog.Actions.PrimaryButton
            type="submit"
            label="Save"
          >
            Save
          </Dialog.Actions.PrimaryButton>
        </Dialog.Actions>
      </Form>
    </Dialog.Content>
  );
}

function DirtyIndicator() {
  const formState = useFormState();
  const isDirty = formState?.isDirty ?? false;

  return (
    <div
      className={`text-xs px-3 py-2 rounded border font-mono transition-colors ${
        isDirty
          ? "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-900/20 dark:text-orange-400"
          : "border-border bg-muted/30 text-muted-foreground"
      }`}
    >
      {isDirty
        ? "⚠️ Form is dirty — close button / overlay are guarded"
        : "✓ Form is clean — close button works normally"}
    </div>
  );
}

// ─── Tab content guard ─────────────────────────────────────────────────────

function TabGuardDemo() {
  const [activeTab, setActiveTab] = useState("profile");

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className="flex flex-col gap-4"
    >
      <Tabs.List>
        <Tabs.Trigger value="profile">Profile</Tabs.Trigger>
        <Tabs.Trigger value="settings">Settings</Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content value="profile">
        <Form
          defaultValues={{ title: "" }}
          dismissGuard
          onSubmit={async () => {}}
        >
          <div className="flex flex-col gap-3">
            <TextField
              name="title"
              label="Title"
              placeholder="Edit this, then switch tabs"
            />
            <DirtyIndicator />
          </div>
        </Form>
      </Tabs.Content>
      <Tabs.Content value="settings">
        <div className="rounded border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          Settings content
        </div>
      </Tabs.Content>
    </Tabs>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────

export function DismissGuardPage() {
  return (
    <ComponentPage
      title="Dismiss Guard"
      description="Protects users from accidentally losing unsaved data by intercepting dialog closes, tab switches, browser tab/window closes, and in-app navigation."
    >
      {/* ── Overview table ─────────────────────────────────────────────── */}
      <ComponentSection
        title="How it works"
        description="One dirty signal activates every supported dismissal path."
      >
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="py-2 px-4 text-left text-xs font-semibold text-foreground">Scenario</th>
                <th className="py-2 px-4 text-left text-xs font-semibold text-foreground">Mechanism</th>
                <th className="py-2 px-4 text-left text-xs font-semibold text-foreground">API</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border">
                <td className="py-3 px-4 text-sm font-medium">Dialog close (X, Escape, overlay)</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  <code className="font-mono bg-muted px-1.5 py-0.5 rounded">DismissableProvider</code> registered on
                  the nearest dismissable surface
                </td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  <code className="font-mono bg-muted px-1.5 py-0.5 rounded">Form dismissGuard</code> or{" "}
                  <code className="font-mono bg-muted px-1.5 py-0.5 rounded">useDismissGuard()</code>
                </td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-3 px-4 text-sm font-medium">Browser / tab close</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  <code className="font-mono bg-muted px-1.5 py-0.5 rounded">
                    window.addEventListener("beforeunload", …)
                  </code>
                </td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  Automatic when <code className="font-mono bg-muted px-1.5 py-0.5 rounded">when</code> is truthy
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-sm font-medium">In-app navigation</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  React Router history block registered by{" "}
                  <code className="font-mono bg-muted px-1.5 py-0.5 rounded">useDismissGuard()</code>
                </td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  Automatic when the guarded component is rendered inside a router
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-sm font-medium">Tab switching</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  <code className="font-mono bg-muted px-1.5 py-0.5 rounded">&lt;Tabs&gt;</code> asks the active dismiss
                  guard before changing value
                </td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  Automatic when guarded content is rendered inside{" "}
                  <code className="font-mono bg-muted px-1.5 py-0.5 rounded">Tabs.Content</code>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ComponentSection>

      {/* ── Tab guard playground ──────────────────────────────────────── */}
      <ComponentSection
        title="Form in Tabs"
        description="Edit the field, then switch tabs — Tabs.Content participates in the same dismiss guard flow."
      >
        <ComponentPreview>
          <TabGuardDemo />
        </ComponentPreview>
      </ComponentSection>

      {/* ── Dialog guard playground ────────────────────────────────────── */}
      <ComponentSection
        title="Form in Dialog"
        description="Open the dialog, edit a field, then try to close it — the guard intercepts and shows a confirm dialog."
      >
        <ComponentPreview>
          <GuardedDialogForm />
        </ComponentPreview>
      </ComponentSection>

      {/* ── Standalone hook ───────────────────────────────────────────── */}
      <ComponentSection
        title="Standalone useDismissGuard"
        description="Use the hook directly — without Form — to guard any custom UI."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <StandaloneDirtyGuard />
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Hook only (no Form)">
              <span className="text-xs text-muted-foreground">
                Call{" "}
                <code className="font-mono bg-muted px-1 py-0.5 rounded">useDismissGuard({"{ when, onAttempt }"})</code>{" "}
                directly.
              </span>
            </ControlRow>
            <p className="text-xs text-muted-foreground pt-1">
              Useful for non-form views (e.g. drag-and-drop canvases, unsaved graph edits).
            </p>
          </ControlPanel>
        </div>
      </ComponentSection>

      {/* ── API reference ─────────────────────────────────────────────── */}
      <ComponentSection title="API Reference">
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="py-2 px-4 text-left text-xs font-semibold text-foreground">Name</th>
                <th className="py-2 px-4 text-left text-xs font-semibold text-foreground">Type</th>
                <th className="py-2 px-4 text-left text-xs font-semibold text-foreground">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border">
                <td className="py-3 px-4 font-mono text-xs">useDismissGuard(options)</td>
                <td className="py-3 px-4 text-xs text-muted-foreground font-mono">Hook</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  Registers dismissable surface, router navigation, and window close guards. Returns{" "}
                  <code className="bg-muted px-1 py-0.5 rounded">{"{ active }"}</code>.
                </td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-3 px-4 font-mono text-xs">options.when</td>
                <td className="py-3 px-4 text-xs text-muted-foreground font-mono">boolean</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">Activates the guard when truthy.</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-3 px-4 font-mono text-xs">options.onAttempt</td>
                <td className="py-3 px-4 text-xs text-muted-foreground font-mono">
                  (proceed: () =&gt; void) =&gt; void
                </td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  Override the default confirm dialog. Call{" "}
                  <code className="bg-muted px-1 py-0.5 rounded">proceed()</code> to allow the close.
                </td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-3 px-4 font-mono text-xs">Form dismissGuard</td>
                <td className="py-3 px-4 text-xs text-muted-foreground font-mono">boolean</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  Activates the guard automatically when <code className="bg-muted px-1 py-0.5 rounded">isDirty</code>{" "}
                  is true. Shows a built-in <code className="bg-muted px-1 py-0.5 rounded">&lt;ConfirmDialog&gt;</code>{" "}
                  on intercept.
                </td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-3 px-4 font-mono text-xs">Dialog onOpenChange</td>
                <td className="py-3 px-4 text-xs text-muted-foreground font-mono">Existing API</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  Calls the nearest <code className="bg-muted px-1 py-0.5 rounded">DismissableProvider</code> before
                  forwarding close events.
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-mono text-xs">Tabs.Content</td>
                <td className="py-3 px-4 text-xs text-muted-foreground font-mono">Existing API</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">
                  Guarded content inside tabs intercepts tab switches through the same dismissable surface.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ComponentSection>
    </ComponentPage>
  );
}
